import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth";
import { auth } from "../firebase/config";
import Config from "../Config";

type User = FirebaseUser & {
  name?: string;
  email: string;
};

type AuthContextType = {
  user: User | null;
  /**
   * Display name for the signed-in user. Read this rather than `user.name`:
   * on the Firebase path the name arrives from `/api/auth/me` after the User is
   * already set (so auth isn't blocked on it), and it's mutated onto the User
   * instance in place — which React cannot observe. This value is backed by
   * distinct state so consumers re-render when the name lands.
   */
  profileName: string | undefined;
  backendToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  needsPayment: boolean;
  accountType: string;
  savedTimeMinutes: number;
  refreshSavedTime: () => Promise<void>;
  refreshSubscriptionStatus: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithKeycloak: () => Promise<void>;
  setTokenFromKeycloak: (token: string) => Promise<void>;
  completeSignup: (token: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  // Display name from /api/auth/me, kept as its own state so React observes it:
  // on the Firebase path it lands after the User is set and is also mutated onto
  // the (unobservable) User instance in place.
  const [displayName, setDisplayName] = useState<string | undefined>(undefined);
  const [backendToken, setBackendToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [needsPayment, setNeedsPayment] = useState(false);
  const [accountType, setAccountType] = useState("free");
  const [savedTimeMinutes, setSavedTimeMinutes] = useState(0);
  // Monotonic id per onAuthStateChanged invocation. Each run captures its id and,
  // after every await, bails if a newer run has started — so a slow/late response
  // for a signed-out or switched-away account can never apply its state (name,
  // token, role) over the current one. A generation counter rather than an
  // auth.currentUser check because it is self-contained and deterministic in tests.
  const authRun = useRef(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const runId = ++authRun.current;
      const superseded = () => authRun.current !== runId;
      if (firebaseUser) {
        // Set user immediately so isAuthenticated resolves as soon as backendToken
        // is also ready, without blocking on the /api/auth/me name look-up below.
        setUser(firebaseUser as User);
        // Clear any prior account's name up front: on a direct account switch
        // (Firebase fires onAuthStateChanged with the new user and no null in
        // between) this prevents the previous name from lingering if the new
        // user's /api/auth/me is slow or fails before it repopulates it.
        setDisplayName(undefined);

        const existingToken = localStorage.getItem("access_token");
        if (existingToken) {
          setBackendToken(existingToken);

          try {
            const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${existingToken}` },
            });
            if (meResponse.ok) {
              const meData = await meResponse.json();
              // Bail if the account changed while /me was in flight, so a stale
              // response can't reapply a switched-away account's identity.
              if (superseded()) return;
              // Name lives in Postgres (its source of truth): mutate it onto the User
              // instance (for user.name readers like Profile after a reload)
              // and drive the re-render off distinct state.
              (firebaseUser as User).name = meData.name ?? undefined;
              setDisplayName(meData.name ?? undefined);
              setIsAdmin(meData.is_admin);
              setAccountType(meData.account_type ?? "free");
              setSavedTimeMinutes(meData.saved_time_minutes ?? 0);
              setNeedsPayment(
                localStorage.getItem("payment_pending") === "true",
              );
            }
          } catch (error) {
            console.error("Error fetching user info:", error);
          }
          if (superseded()) return;
          setIsLoading(false);
          return;
        }

        try {
          const idToken = await firebaseUser.getIdToken();
          // A newer run may have signed a different account in while the token
          // refresh was in flight — its session must not be cleared below.
          if (superseded()) return;
          if (!firebaseUser.emailVerified) {
            console.log("Email not verified, skipping backend authentication");
            setBackendToken(null);
            localStorage.removeItem("access_token");
            setIsLoading(false);
            return;
          }

          const lastSignInTime = firebaseUser.metadata?.lastSignInTime;
          const body = lastSignInTime
            ? JSON.stringify({
                last_sign_in_time: new Date(lastSignInTime).toISOString(),
              })
            : null;

          let response = await fetch(
            `${Config.API_URL}/api/auth/firebase/login`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
                ...(body ? { "Content-Type": "application/json" } : {}),
              },
              ...(body ? { body } : {}),
            },
          );

          if (response.status === 404) {
            console.log("User not found in PostgreSQL, creating account...");
            response = await fetch(
              `${Config.API_URL}/api/auth/firebase/signup`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${idToken}`,
                  ...(body ? { "Content-Type": "application/json" } : {}),
                },
                ...(body ? { body } : {}),
              },
            );
          }

          if (response.ok) {
            const data = await response.json();
            // Don't persist a switched-away account's token over the current one.
            if (superseded()) return;
            setBackendToken(data.access_token);
            localStorage.setItem("access_token", data.access_token);

            const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${data.access_token}` },
            });
            if (meResponse.ok) {
              const meData = await meResponse.json();
              if (superseded()) return;
              // Name lives in Postgres (its source of truth): mutate it onto the User
              // instance (for user.name readers like Profile after a reload)
              // and drive the re-render off distinct state.
              (firebaseUser as User).name = meData.name ?? undefined;
              setDisplayName(meData.name ?? undefined);
              setIsAdmin(meData.is_admin);
              setAccountType(meData.account_type ?? "free");
              setSavedTimeMinutes(meData.saved_time_minutes ?? 0);
              setNeedsPayment(
                localStorage.getItem("payment_pending") === "true",
              );
            }
          } else {
            console.error("Failed to get backend token");
            // Don't clear the current account's session from a stale/switched-away run.
            if (superseded()) return;
            setBackendToken(null);
            localStorage.removeItem("access_token");
          }
        } catch (error) {
          console.error("Error getting backend token:", error);
          if (superseded()) return;
          setBackendToken(null);
          localStorage.removeItem("access_token");
        }
      } else {
        // No Firebase user — check if there's a valid Keycloak backend token persisted
        const existingToken = localStorage.getItem("access_token");
        if (existingToken) {
          try {
            const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${existingToken}` },
            });
            if (meResponse.ok) {
              const meData = await meResponse.json();
              if (superseded()) return;
              setBackendToken(existingToken);
              setUser({ uid: "keycloak-user", email: meData.email, name: meData.name } as User);
              setDisplayName(meData.name ?? undefined);
              setIsAdmin(meData.is_admin);
              setAccountType(meData.account_type ?? "free");
              setSavedTimeMinutes(meData.saved_time_minutes ?? 0);
              setNeedsPayment(
                localStorage.getItem("payment_pending") === "true",
              );
              setIsLoading(false);
              return;
            }
          } catch (error) {
            console.error("Error validating persisted token:", error);
          }
        }
        // A newer run may have established a session while the validation above
        // was in flight; this stale run must not tear it down. A no-op on the
        // synchronous path (nothing persisted), where no await happened.
        if (superseded()) return;
        // Signed out with no valid persisted session — clear everything, so no
        // part of the previous account (name, role, plan) survives into the next
        // one within the same page session.
        setUser(null);
        setDisplayName(undefined);
        setBackendToken(null);
        setIsAdmin(false);
        setNeedsPayment(false);
        setAccountType("free");
        setSavedTimeMinutes(0);
        localStorage.removeItem("access_token");
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // listen for changes to access_token in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "access_token") {
        if (event.newValue === null) {
          console.log("Session ended in another tab, logging out...");
          setBackendToken(null);
          setUser(null);
          setDisplayName(undefined);
          setIsAdmin(false);
          setNeedsPayment(false);
          setAccountType("free");
          setSavedTimeMinutes(0);
          signOut(auth).catch((error) => {
            console.error("Error signing out after cross-tab logout:", error);
          });
        } else if (event.newValue !== backendToken) {
          console.log("Session changed in another tab, updating token...");
          setBackendToken(event.newValue);
          window.location.reload();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [backendToken]);

  // login
  const login = useCallback(async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );

    if (!userCredential.user.emailVerified) {
      await signOut(auth);
      throw new Error(
        "Please verify your email before logging in. Check your inbox for the verification link.",
      );
    }

    const idToken = await userCredential.user.getIdToken();
    const lastSignInTime = userCredential.user.metadata?.lastSignInTime;
    const body = lastSignInTime
      ? JSON.stringify({
          last_sign_in_time: new Date(lastSignInTime).toISOString(),
        })
      : null;

    let response = await fetch(`${Config.API_URL}/api/auth/firebase/login`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body } : {}),
    });

    if (response.status === 404) {
      console.log("User not found in PostgreSQL, creating account...");
      response = await fetch(`${Config.API_URL}/api/auth/firebase/signup`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        ...(body ? { body } : {}),
      });
    }

    if (!response.ok) {
      throw new Error("Backend authentication failed");
    }

    const data = await response.json();
    setBackendToken(data.access_token);
    localStorage.setItem("access_token", data.access_token);

    const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${data.access_token}` },
    });
    if (meResponse.ok) {
      const meData = await meResponse.json();
      setIsAdmin(meData.is_admin);
      setAccountType(meData.account_type ?? "free");
      setNeedsPayment(
        meData.account_type === "individual" &&
          meData.subscription_status !== "active",
      );
    }
  }, []);

  // keycloak login
  const loginWithKeycloak = useCallback(async () => {
    try {
      const response = await fetch(`${Config.API_URL}/api/auth/keycloak/vars`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to get Keycloak configuration");
      }

      const data = await response.json();
      const { url, realm, client, redirect } = data;

      const authUrl = new URL(
        `${url}/realms/${realm}/protocol/openid-connect/auth`,
      );
      authUrl.searchParams.append("client_id", client);
      authUrl.searchParams.append("redirect_uri", redirect);
      authUrl.searchParams.append("response_type", "code");
      authUrl.searchParams.append("scope", "openid profile email");

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error("Error initiating Keycloak login:", error);
      throw error;
    }
  }, []);

  // refresh saved time from backend
  const refreshSavedTime = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    try {
      const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meResponse.ok) {
        const meData = await meResponse.json();
        setSavedTimeMinutes(meData.saved_time_minutes ?? 0);
      }
    } catch (error) {
      console.error("Error refreshing saved time:", error);
    }
  }, []);

  // re-fetch subscription status from Stripe (via billing/status) and update needsPayment
  const refreshSubscriptionStatus = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    try {
      const res = await fetch(`${Config.API_URL}/api/billing/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "active") {
          localStorage.removeItem("payment_pending");
          setNeedsPayment(false);
        }
      }
      // Also refresh account type from /api/auth/me since it may have changed after payment
      const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meResponse.ok) {
        const meData = await meResponse.json();
        setAccountType(meData.account_type ?? "free");
      }
    } catch (error) {
      console.error("Error refreshing subscription status:", error);
    }
  }, []);

  // keycloak token
  const setTokenFromKeycloak = useCallback(async (token: string) => {
    setBackendToken(token);
    localStorage.setItem("access_token", token);
    try {
      const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meResponse.ok) {
        const meData = await meResponse.json();
        setUser({ uid: "keycloak-user", email: meData.email, name: meData.name } as User);
        setIsAdmin(meData.is_admin);
        setAccountType(meData.account_type ?? "free");
        setSavedTimeMinutes(meData.saved_time_minutes ?? 0);
        setNeedsPayment(
          meData.account_type === "individual" &&
            meData.subscription_status !== "active",
        );
        return;
      }
    } catch (error) {
      console.error("Error fetching PSDI user info:", error);
    }
    setUser({ uid: "keycloak-user" } as User);
  }, []);

  // called after OTP verification to set auth state without a page reload
  const completeSignup = useCallback(async (token: string) => {
    setBackendToken(token);
    localStorage.setItem("access_token", token);
    try {
      const meResponse = await fetch(`${Config.API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (meResponse.ok) {
        const meData = await meResponse.json();
        // Show the name captured at signup without waiting for a reload.
        // Mirror it onto the current Firebase User instance too — the same
        // object `user` state holds — so consumers that read `user.name`
        // (e.g. Profile, ShareDocumentModal) see it, matching the two
        // onAuthStateChanged branches rather than only populating profileName.
        if (auth.currentUser) {
          (auth.currentUser as User).name = meData.name ?? undefined;
        }
        setDisplayName(meData.name ?? undefined);
        setIsAdmin(meData.is_admin);
        setAccountType(meData.account_type ?? "free");
        setSavedTimeMinutes(meData.saved_time_minutes ?? 0);
        setNeedsPayment(
          localStorage.getItem("payment_pending") === "true" ||
            (meData.account_type === "individual" &&
              meData.subscription_status !== "active"),
        );
      }
    } catch (error) {
      console.error("Error fetching user info after signup:", error);
    }
  }, []);

  // logout
  const logout = useCallback(async () => {
    await signOut(auth);
    setBackendToken(null);
    setDisplayName(undefined);
    setIsAdmin(false);
    setNeedsPayment(false);
    setAccountType("free");
    setSavedTimeMinutes(0);
    localStorage.removeItem("access_token");
  }, []);

  // Backed by observable state (set from /api/auth/me), falling back to
  // user.name — which the Keycloak paths set on a fresh user object React can
  // see, and which the Firebase path mutates in place (so it survives a reload).
  const profileName = displayName ?? user?.name;

  const value: AuthContextType = {
    user,
    profileName,
    backendToken,
    isAuthenticated: !!user && !!backendToken,
    isLoading,
    isAdmin,
    needsPayment,
    accountType,
    savedTimeMinutes,
    refreshSavedTime,
    refreshSubscriptionStatus,
    login,
    loginWithKeycloak,
    setTokenFromKeycloak,
    completeSignup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
